<script setup lang="ts">
import { onBeforeMount, onMounted, type PropType, type Ref, ref, watch } from 'vue'

import AdminUnits from './AdminUnits.vue'
import type { AxiosResponse } from 'axios'
import PhoneInput from '@/components/PhoneInput/PhoneInput.vue'
import { dateFormat, sqlDateFormat } from '@/commons/composables/transformations.ts'

import type { Country} from '@/commons/types/das.ts'
import {
  FormBeneficialOwnerPerson,
  Nationality,
  NIRAPerson
} from "@/domains/home/services/nir/forms/components/widgets/types";
import {ninCalculator} from "@/composables/ninCalculator.ts";
import api from "@/app/services/api.ts";

const props = defineProps({
  active: Boolean,
  modelValue: {
    type: Object as PropType<FormBeneficialOwnerPerson>,
  },
  data: {
    type: Object as PropType<FormBeneficialOwnerPerson>,
  },
})

const nira = ninCalculator()
const countries: Ref<Country[]> = ref([])
const nationalities: Ref<Nationality[]> = ref([])

const emit = defineEmits<{
  (e: 'update:modelValue', value: FormBeneficialOwnerPerson): void
}>()

const individual = ref<FormBeneficialOwnerPerson>({
  givenName: '',
  surname: '',
  middleNames: '',
  gender: '',
  dateOfBirth: '',
  nationalityId: '',
  countryId: '',
  idType: '',
  idNumber: '',
  email: '',
  phone: '',
  votingRightsPercentage: 0,
  ownershipPercentage: 0,
  address: {
    districtId: '',
    countyId: '',
    subCountyId: '',
    parishId: '',
    villageId: '',
    description: '',
  },
})

onMounted(() => {
  fetchCountries()
  fetchNationalities()
})

watch(
  individual,
  (newValue) => {
    individual.value = newValue
    emit('update:modelValue', newValue)
  },
  { deep: true },
)

watch(
  () => props.data,
  () => {
    individual.value = props.data ?? individual.value
  },
)

const ninPerson = ref<NIRAPerson | undefined>()
const isSearching = ref(false)

// ── Validation ─────────────────────────────────────────────────────────────

const errors = ref({
  idType: '',
  idNumber: '',
  nationalityId: '',
  countryId: '',
  givenName: '',
  surname: '',
  gender: '',
  dateOfBirth: '',
  email: '',
  phone: '',
})

function validateIdType() {
  errors.value.idType = !individual.value.idType ? 'ID type is required' : ''
}

function validateIdNumber() {
  errors.value.idNumber = !individual.value.idNumber?.trim() ? 'ID number is required' : ''
}

function validateCountryId() {
  errors.value.nationalityId = !individual.value.nationalityId
    ? 'Country of origin is required'
    : ''
}

function validateGivenName() {
  errors.value.givenName = !individual.value.givenName?.trim() ? 'Given name is required' : ''
}

function validateSurname() {
  errors.value.surname = !individual.value.surname?.trim() ? 'Surname is required' : ''
}

function validateGender() {
  errors.value.gender = !individual.value.gender ? 'Gender is required' : ''
}

function validateDateOfBirth() {
  errors.value.dateOfBirth = !individual.value.dateOfBirth ? 'Date of birth is required' : ''
}

function validateEmail() {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!individual.value.email) {
    errors.value.email = 'Email is required'
  } else if (!emailRegex.test(individual.value.email)) {
    errors.value.email = 'Please enter a valid email address'
  } else {
    errors.value.email = ''
  }
}

// ── NIRA / ID lookup ────────────────────────────────────────────────────────

function verifyId() {
  if (individual.value.idType != 'national_id') {
    return
  }
  isSearching.value = true
  let payload = {
    nin: individual.value.idNumber,
    reference: nira.getNinReference(),
  }
  return api
    .post(`/integration/api/nira`, payload)
    .then((response: AxiosResponse<NIRAPerson>) => {
      ninPerson.value = response.data

      individual.value.givenName = ninPerson.value.givenNames
      individual.value.middleNames = ninPerson.value.middleName || ''
      individual.value.surname = ninPerson.value.surname
      individual.value.dateOfBirth = dateFormat(ninPerson.value.dateOfBirth)
      individual.value.gender = ninPerson.value.gender == 'M' ? 'male' : 'female'
    })
    .finally(() => {
      isSearching.value = false
    })
}

const fetchNationalities = async () => {
  return api
    .get(`/configuration/nationality/list`)
    .then((response: AxiosResponse<Nationality[]>) => {
      const raw = Array.isArray(response.data) ? response.data : ((response.data as any).data ?? [])
      nationalities.value = raw.filter((n: any) => n != null)
    })
}

const fetchCountries = async () => {
  return api.get(`/configuration/address/country`).then((response: AxiosResponse<Country[]>) => {
    const raw = Array.isArray(response.data) ? response.data : ((response.data as any).data ?? [])
    countries.value = raw.filter((n: any) => n != null)
  })
}

function checkNationality() {
  if (individual.value.nationalityId?.toLowerCase().includes('Ugandan')) {
    individual.value.idType = 'national_id'
  } else {
    individual.value.idType = ''
  }
}
</script>

<template>
  <div class="flex flex-col space-y-3">
    <div class="border border-gray-200 rounded-lg p-4">
      <div class="grid grid-cols-2 gap-3">
        <div class="col-span-2">
          <div class="grid grid-cols-1 gap-3">
            <!-- Country -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-earth-africa text-gray-400 mr-1"></i>
                Nationality <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <select
                v-model="individual.nationalityId"
                required
                @change="checkNationality"
                @blur="validateCountryId"
                :class="[
                  'w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed',
                  errors.nationalityId
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300',
                ]"
              >
                <option
                  v-for="(nationality, idx) in nationalities"
                  :key="idx"
                  :value="nationality.name"
                >
                  {{ nationality.name }}
                </option>
              </select>
              <p v-if="errors.nationalityId" class="mt-1 text-xs text-red-600">
                {{ errors.nationalityId }}
              </p>
            </div>
          </div>
        </div>
        <!-- ID Type & Number Row -->
        <div class="col-span-2">
          <div class="grid grid-cols-2 gap-3">
            <!-- ID Type -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-id-badge text-gray-400 mr-1"></i>
                ID Type <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <select
                v-model="individual.idType"
                required
                @blur="validateIdType"
                :class="[
                  'w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed',
                  errors.idType
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300',
                ]"
              >
                <option value="">Select ID Type</option>
                <option value="national_id" v-if="individual.nationalityId == 'Ugandan'">
                  National ID
                </option>
                <option value="passport" v-if="individual.nationalityId != 'Ugandan'">
                  Passport
                </option>
                <option value="alien_id" v-if="individual.nationalityId != 'Ugandan'">
                  Alien ID
                </option>
              </select>
              <p v-if="errors.idType" class="mt-1 text-xs text-red-600">{{ errors.idType }}</p>
            </div>

            <!-- ID Number -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-hashtag text-gray-400 mr-1"></i>
                ID Number <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <div class="flex gap-2">
                <input
                  v-model="individual.idNumber"
                  @change="verifyId()"
                  @keyup.enter="verifyId()"
                  @blur="validateIdNumber"
                  type="text"
                  required
                  placeholder="Enter ID number"
                  :class="[
                    'w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary',
                    errors.idNumber
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300',
                  ]"
                />
                <span class="loading-spinner" v-if="isSearching">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </span>
              </div>
              <p v-if="errors.idNumber" class="mt-1 text-xs text-red-600">{{ errors.idNumber }}</p>
            </div>
          </div>
        </div>

        <!-- Names Row -->
        <div class="col-span-2">
          <div class="grid grid-cols-3 gap-3">
            <!-- Given Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-user text-gray-400 mr-1"></i>
                Given Name <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <input
                v-model="individual.givenName"
                :disabled="individual.idType == 'national_id'"
                @blur="validateGivenName"
                type="text"
                required
                placeholder="Enter given name"
                :class="[
                  'w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed',
                  errors.givenName
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300',
                ]"
              />
              <p v-if="errors.givenName" class="mt-1 text-xs text-red-600">
                {{ errors.givenName }}
              </p>
            </div>

            <!-- Surname -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-user text-gray-400 mr-1"></i>
                Surname <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <input
                v-model="individual.surname"
                :disabled="individual.idType == 'national_id'"
                @blur="validateSurname"
                type="text"
                required
                placeholder="Enter surname"
                :class="[
                  'w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed',
                  errors.surname
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300',
                ]"
              />
              <p v-if="errors.surname" class="mt-1 text-xs text-red-600">{{ errors.surname }}</p>
            </div>

            <!-- Middle Names -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-user text-gray-400 mr-1"></i>
                Middle Names
              </label>
              <input
                v-model="individual.middleNames"
                :disabled="individual.idType == 'national_id'"
                type="text"
                placeholder="Enter middle names (optional)"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        <!-- Gender -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-venus-mars text-gray-400 mr-1"></i>
            Gender <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <select
            v-model="individual.gender"
            :disabled="individual.idType == 'national_id'"
            @blur="validateGender"
            required
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed',
              errors.gender
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300',
            ]"
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          <p v-if="errors.gender" class="mt-1 text-xs text-red-600">{{ errors.gender }}</p>
        </div>

        <!-- Date of Birth -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-calendar text-gray-400 mr-1"></i>
            Date of Birth <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <VueDatePicker
            v-model="individual.dateOfBirth"
            format="dd-MM-yyyy"
            model-type="format"
            :disabled="individual.idType == 'national_id'"
            auto-apply
            :max-date="new Date(new Date().setFullYear(new Date().getFullYear() - 18))"
            :min-date="new Date(new Date().setFullYear(new Date().getFullYear() - 150))"
            @update:model-value="validateDateOfBirth"
          ></VueDatePicker>
          <p v-if="errors.dateOfBirth" class="mt-1 text-xs text-red-600">
            {{ errors.dateOfBirth }}
          </p>
        </div>

        <!-- Email -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-envelope text-gray-400 mr-1"></i>
            Email <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <input
            v-model="individual.email"
            type="email"
            required
            placeholder="Enter email"
            @blur="validateEmail"
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary',
              errors.email
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300',
            ]"
          />
          <p v-if="errors.email" class="mt-1 text-xs text-red-600">{{ errors.email }}</p>
        </div>

        <!-- Phone -->
        <div>
          <PhoneInput
            v-model="individual.phone"
            label="Phone"
            defaultCountry="+256"
            placeholder="Enter phone number"
            :required="true"
            size="sm"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-earth-africa text-gray-400 mr-1"></i>
            Country of Residence <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <select
            v-model="individual.countryId"
            required
            @blur="validateCountryId"
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed',
              errors.nationalityId ? 'border-red-500' : 'border-gray-300',
            ]"
          >
            <option v-for="(country, idx) in countries" :key="idx" :value="country.id">
              {{ country.name }}
            </option>
          </select>
          <p v-if="errors.countryId" class="mt-1 text-xs text-red-600">
            {{ errors.countryId }}
          </p>
        </div>

        <!-- Address -->
        <div class="col-span-2">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fa-solid fa-map-marker-alt text-gray-400 mr-1"></i>
            Address <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <AdminUnits
            :active="true"
            :isUganda="countries.find((c) => c.id == individual.countryId)?.name == 'Uganda'"
            v-model="individual.address"
            :data="individual.address"
          />
        </div>

        <div class="col-span-2">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-user text-gray-400 mr-1"></i>
                Voting Rights Percentage <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <input
                v-model="individual.votingRightsPercentage"
                type="text"
                required
                placeholder="Enter given name"
                :class="[
                  'w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed',
                  errors.givenName
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300',
                ]"
              />
              <p v-if="errors.givenName" class="mt-1 text-xs text-red-600">
                {{ errors.givenName }}
              </p>
            </div>

            <!-- Surname -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                <i class="fa-solid fa-user text-gray-400 mr-1"></i>
                Ownership Percentage <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
              </label>
              <input
                v-model="individual.ownershipPercentage"
                type="text"
                required
                placeholder="Enter surname"
                :class="[
                  'w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed',
                  errors.surname
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300',
                ]"
              />
              <p v-if="errors.surname" class="mt-1 text-xs text-red-600">{{ errors.surname }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.loading-spinner {
  display: inline-block;
  position: relative;
  width: 40px;
  height: 40px;
}

.loading-spinner div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 32px;
  height: 32px;
  margin: 4px;
  border: 3px solid;
  border-radius: 50%;
  animation: loading-spinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: #3b82f6 transparent transparent transparent;
}

.loading-spinner div:nth-child(1) {
  animation-delay: -0.45s;
}

.loading-spinner div:nth-child(2) {
  animation-delay: -0.3s;
}

.loading-spinner div:nth-child(3) {
  animation-delay: -0.15s;
}

@keyframes loading-spinner {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-spinner {
  animation: pulse-glow 2s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>